import { type HTMLAttributes, forwardRef } from 'react';
import { cn } from '@/lib/utils';
import { User } from 'lucide-react';
import { createAvatar } from '@dicebear/core';
import * as lorelei from '@dicebear/lorelei';

export interface AvatarProps extends HTMLAttributes<HTMLDivElement> {
  src?: string | null;
  alt?: string;
  name?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  ring?: boolean;
  status?: 'online' | 'offline' | 'busy' | 'away';
}

const Avatar = forwardRef<HTMLDivElement, AvatarProps>(
  ({ className, src, alt, name, size = 'md', ring = false, status, ...props }, ref) => {
    const sizes = {
      xs: 'h-6 w-6 text-2xs',
      sm: 'h-7 w-7 text-xs',
      md: 'h-9 w-9 text-sm',
      lg: 'h-11 w-11 text-base',
      xl: 'h-14 w-14 text-lg',
    };

    const iconSizes = {
      xs: 'h-3 w-3',
      sm: 'h-3.5 w-3.5',
      md: 'h-4 w-4',
      lg: 'h-5 w-5',
      xl: 'h-6 w-6',
    };

    const statusSizes = {
      xs: 'h-1.5 w-1.5 ring-1',
      sm: 'h-2 w-2 ring-1',
      md: 'h-2.5 w-2.5 ring-2',
      lg: 'h-3 w-3 ring-2',
      xl: 'h-3.5 w-3.5 ring-2',
    };

    const statusColors = {
      online: 'bg-success-500',
      offline: 'bg-secondary-400 dark:bg-secondary-600',
      busy: 'bg-danger-500',
      away: 'bg-warning-500',
    };

    // Generate DiceBear illustrated avatar SVG as data URI
    const getDiceBearSrc = (seed: string) => {
      const svg = createAvatar(lorelei, { seed, size: 128 }).toString();
      return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
    };

    const avatarSrc = src || (name ? getDiceBearSrc(name) : null);

    return (
      <div
        ref={ref}
        className={cn(
          'relative flex shrink-0 items-center justify-center overflow-hidden rounded-full',
          sizes[size],
          !avatarSrc ? 'bg-gray-200 dark:bg-secondary-700' : 'bg-gray-100 dark:bg-secondary-800',
          ring && 'ring-2 ring-white dark:ring-secondary-900',
          className
        )}
        {...props}
      >
        {avatarSrc ? (
          <img
            src={avatarSrc}
            alt={alt || name || 'Avatar'}
            className="h-full w-full object-cover"
          />
        ) : (
          <User className={cn('text-gray-400 dark:text-secondary-500', iconSizes[size])} />
        )}
        {status && (
          <span
            className={cn(
              'absolute bottom-0 right-0 rounded-full ring-white dark:ring-secondary-900',
              statusSizes[size],
              statusColors[status]
            )}
          />
        )}
      </div>
    );
  }
);

Avatar.displayName = 'Avatar';

// Avatar Group for showing multiple avatars
interface AvatarGroupProps extends HTMLAttributes<HTMLDivElement> {
  max?: number;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  children: React.ReactNode;
}

const AvatarGroup = forwardRef<HTMLDivElement, AvatarGroupProps>(
  ({ className, max = 4, size = 'md', children, ...props }, ref) => {
    const childrenArray = Array.isArray(children) ? children : [children];
    const displayChildren = childrenArray.slice(0, max);
    const remaining = childrenArray.length - max;

    const spacing = {
      xs: '-space-x-1.5',
      sm: '-space-x-2',
      md: '-space-x-2.5',
      lg: '-space-x-3',
    };

    return (
      <div ref={ref} className={cn('flex', spacing[size], className)} {...props}>
        {displayChildren}
        {remaining > 0 && (
          <div
            className={cn(
              'relative flex shrink-0 items-center justify-center rounded-full',
              'bg-secondary-200 dark:bg-secondary-700',
              'text-secondary-600 dark:text-secondary-300',
              'ring-2 ring-white dark:ring-secondary-900',
              'font-medium',
              size === 'xs' && 'h-6 w-6 text-2xs',
              size === 'sm' && 'h-7 w-7 text-xs',
              size === 'md' && 'h-9 w-9 text-xs',
              size === 'lg' && 'h-11 w-11 text-sm'
            )}
          >
            +{remaining}
          </div>
        )}
      </div>
    );
  }
);

AvatarGroup.displayName = 'AvatarGroup';

export { Avatar, AvatarGroup };
